import { motion, useInView } from "framer-motion";
import React, { useRef, useState } from "react";
import { FaArrowRight, FaClock } from "react-icons/fa";
import { STANDARD_VARIANTS } from "../constants/animations";
import { CONTAINER_STYLES, TYPOGRAPHY_STYLES, CARD_STYLES } from "../constants/styles";

interface CaseStudy {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  challenge: string;
  solution: string;
  results: {
    metric: string;
    value: string;
  }[];
  technologies: string[];
  image: string;
  category: "mobile" | "ai" | "android" | "web";
  timeline: string;
}

const caseStudies: CaseStudy[] = [
  {
    id: 1,
    title: "Always On AMOLED",
    subtitle: "Mobile App with 20M+ Downloads",
    description: "The most popular always-on display app for Android. Grew it solo for eight years, then sold it in 2023.",
    challenge: "Keep an always-on display readable on any Android device without draining the battery.",
    solution: "Wrote power-management logic tuned per device family, used undocumented Android APIs where the platform fell short, and kept a Material Design interface working across every Android version.",
    results: [
      { metric: "Downloads", value: "20M+" },
      { metric: "Countries", value: "180+" },
      { metric: "2023 outcome", value: "Acquired" },
    ],
    technologies: ["Android", "Material Design", "Low-Level", "Battery Optimization"],
    image: "alwaysonamoled.webp",
    category: "android",
    timeline: "8 years",
  },
  {
    id: 2,
    title: "Mixtiles AI Backend",
    subtitle: "Production AI for Millions of Users",
    description: "The AI backend behind Mixtiles photo products: gallery understanding, face and trip detection, and media pipelines for millions of users.",
    challenge: "Process photos and videos for millions of users while actually understanding what's in their galleries.",
    solution: "Python backend on AWS CDK. AI heuristics detect trips, faces, and the people who matter most in a user's gallery, and models for photo enhancement, timelapses, and artistic filters run in the same pipeline.",
    results: [
      { metric: "Users served", value: "Millions" },
      { metric: "Models in production", value: "CLIP + LLMs" },
      { metric: "Infrastructure as code", value: "AWS CDK" },
    ],
    technologies: ["AI/ML", "DevOps", "Data Science", "Production Systems"],
    image: "mixtiles.png",
    category: "web",
    timeline: "Current",
  },
  {
    id: 3,
    title: "Hadasha News AI",
    subtitle: "Consensus-Only AI News App",
    description: "A Hebrew news app that reads every major outlet and publishes only the consensus: what all sources agree actually happened.",
    challenge: "Cut through outlet bias and information overload without hiring a newsroom.",
    solution: "An AI pipeline ingests the day's articles from every source, clusters them by topic, and writes one story per topic containing only the facts all sources agree on.",
    results: [
      { metric: "AI news pipeline", value: "Daily" },
      { metric: "Consensus synthesis", value: "Multi-source" },
      { metric: "Platforms", value: "iOS & Android" },
    ],
    technologies: ["AI/ML", "NLP", "Python", "AWS", "Flutter", "UI/UX Design"],
    image: "hadasha.webp",
    category: "mobile",
    timeline: "4 months",
  },
  {
    id: 4,
    title: "Mobile MCP Framework",
    subtitle: "Open-Source MCP for Android",
    description: "The first framework that lets LLMs running on a phone discover and call other Android apps' capabilities.",
    challenge: "Mobile platforms have no subprocess model, so desktop MCP servers can't run there.",
    solution: "A two-sided framework built on Android AIDL and an MCP protocol adaptation: apps declare capabilities through annotations, and local LLMs discover and call them.",
    results: [
      { metric: "On-device AI", value: "Local LLMs" },
      { metric: "Idea to release", value: "3 weeks" },
      { metric: "Protocol bridge", value: "AIDL + MCP" },
    ],
    technologies: ["Android", "Kotlin", "MCP", "Open Source", "Mobile LLMs"],
    image: "mobilemcp.webp",
    category: "ai",
    timeline: "3 weeks",
  },
];

const categoryLabels: Record<CaseStudy["category"], string> = {
  android: "Android",
  ai: "AI",
  mobile: "Mobile",
  web: "Web",
};

const StatsBlock: React.FC<{ study: CaseStudy; featured?: boolean }> = ({
  study,
  featured = false,
}) => (
  <div
    className={
      featured
        ? "flex flex-wrap lg:flex-col gap-x-8 gap-y-4 lg:gap-y-8 mb-6 lg:mb-0 lg:justify-center"
        : "flex flex-wrap gap-x-8 gap-y-3 mb-6"
    }
  >
    {study.results.map((result, idx) => (
      <div key={idx}>
        <div
          className={`font-display font-bold tracking-tight text-accent-900 dark:text-surface-50 ${
            featured ? "text-2xl lg:text-3xl" : "text-xl"
          }`}
        >
          {result.value}
        </div>
        <div className="text-sm text-accent-500 dark:text-accent-400">
          {result.metric}
        </div>
      </div>
    ))}
  </div>
);

const CaseStudyCard: React.FC<{ study: CaseStudy; featured?: boolean }> = ({
  study,
  featured = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <motion.div
      variants={STANDARD_VARIANTS.itemMedium}
      className={`${CARD_STYLES.interactive} overflow-hidden hover:shadow-2xl transition-all duration-500 ${
        featured ? "lg:col-span-2" : ""
      }`}
    >
      <div
        className={`p-6 ${
          featured ? "lg:p-8 lg:grid lg:grid-cols-[5fr,2fr] lg:gap-12" : ""
        }`}
      >
        <div>
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <span className="inline-block bg-surface-100 dark:bg-accent-700 text-accent-600 dark:text-accent-300 text-sm px-3 py-1 rounded-full mb-2">
              {categoryLabels[study.category]}
            </span>
            <h3 className="text-2xl font-bold tracking-tight text-accent-900 dark:text-surface-50 mb-2">
              {study.title}
            </h3>
            <p className="text-primary-600 dark:text-primary-400 font-semibold mb-3">
              {study.subtitle}
            </p>
          </div>
          <div className="text-right text-sm text-accent-500 dark:text-accent-400">
            <FaClock className="inline mr-1" />
            {study.timeline}
          </div>
        </div>

        <p className="text-accent-700 dark:text-accent-300 mb-6 leading-relaxed max-w-prose">
          {study.description}
        </p>

        {featured ? (
          <div className="lg:hidden">
            <StatsBlock study={study} />
          </div>
        ) : (
          <StatsBlock study={study} />
        )}

        <motion.div
          initial={false}
          animate={{ height: isExpanded ? "auto" : 0, opacity: isExpanded ? 1 : 0 }}
          className="overflow-hidden"
        >
          <div className="border-t border-surface-200 dark:border-accent-700 pt-6 mb-6">
            <div className="mb-4">
              <h4 className="font-semibold text-accent-900 dark:text-surface-50 mb-2">
                Challenge
              </h4>
              <p className="text-accent-700 dark:text-accent-300 text-sm">
                {study.challenge}
              </p>
            </div>
            <div className="mb-4">
              <h4 className="font-semibold text-accent-900 dark:text-surface-50 mb-2">
                Solution
              </h4>
              <p className="text-accent-700 dark:text-accent-300 text-sm">
                {study.solution}
              </p>
            </div>
          </div>
        </motion.div>

        <div className="flex flex-wrap gap-2 mb-6">
          {study.technologies.map((tech, idx) => (
            <span
              key={idx}
              className="bg-surface-100 dark:bg-accent-700 text-accent-700 dark:text-accent-300 text-xs px-3 py-1 rounded-full"
            >
              {tech}
            </span>
          ))}
        </div>

        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold transition-colors duration-200"
        >
          {isExpanded ? "Show Less" : "View Details"}
          <FaArrowRight className={`ml-2 transition-transform duration-200 ${isExpanded ? "rotate-90" : ""}`} />
        </button>
        </div>

        {featured && (
          <div className="hidden lg:flex">
            <StatsBlock study={study} featured />
          </div>
        )}
      </div>
    </motion.div>
  );
};

const CaseStudies: React.FC = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  return (
    <motion.section
      ref={ref}
      className={CONTAINER_STYLES.section}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={STANDARD_VARIANTS.container}
    >
      <div className={CONTAINER_STYLES.contentWide}>
        <motion.div variants={STANDARD_VARIANTS.item} className="text-center mb-16">
          <h2 className={TYPOGRAPHY_STYLES.sectionTitle}>
            Case Studies
          </h2>
          <p className={TYPOGRAPHY_STYLES.sectionSubtitle}>
            Four projects, from problem to shipped product, with the numbers to show for it.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {caseStudies.map((study, index) => (
            <CaseStudyCard
              key={study.id}
              study={study}
              // Wide horizontal layout for the flagship and, when the count is
              // even (flagship + odd remainder), for the last card — otherwise
              // it would sit orphaned next to an empty grid cell.
              featured={
                index === 0 ||
                (caseStudies.length % 2 === 0 && index === caseStudies.length - 1)
              }
            />
          ))}
        </div>
      </div>
    </motion.section>
  );
};

export default CaseStudies;